// Which engine warnings to show beneath the timeline's "could not build a day"
// empty state. The empty state renders its own calm primary message, so the
// engine's generic fresh-plan fallback only duplicates it, and the environmental
// notes ("live lift status unavailable", "flat light today") describe a plan
// that does not exist. Both are hidden. Genuinely useful, plan-independent
// warnings (notably the editor reject) are kept so the editor still explains why
// an edit was dropped. Pure and string-keyed so it is unit-tested without React.

const HIDDEN_WHEN_EMPTY: readonly string[] = [
  "could not build a full day from this start point", // duplicates the primary empty-state line
  "Live lift status is unavailable", // meaningless with no plan to "assume lifts for"
  "Flat light today", // an environmental note with no runs to have favoured
];

export type RouteUnavailableReason = "lifts-closed";

export const CLOSED_LIFTS_SUMMARY = "The mapped lifts that fit these settings are reported closed.";
export const CLOSED_LIFTS_GUIDANCE = "Check the operator's published opening dates and plan again when these lifts operate.";

export function visibleEmptyStateWarnings(warnings: string[]): string[] {
  return warnings.filter((w) => !HIDDEN_WHEN_EMPTY.some((hidden) => w.includes(hidden)));
}

/**
 * Whether all scored ski runs score zero despite scoring being available. The aggregate
 * cannot identify a cause: snow, wind, storms or other forecast limits may
 * contribute. The empty state must describe conditions without inferring snow
 * depth or suggesting settings can bypass them. Missing forecasts are separate.
 *
 * Every run that is not Closed and has a real score counts (open, unknown, hold or
 * scheduled). A Closed run (status "closed") and a run without a score (a null daily, for
 * example "Score unavailable") are not zero scores, so a closed resort or a withheld score
 * never reads as poor forecast conditions. When no run has a real score this is false.
 */
export function noScorableSkiTerrain(
  sport: "ski" | "mtb",
  trailScoresAvailable: boolean,
  trails: readonly { score: number | null; status?: string | null }[],
): boolean {
  if (sport !== "ski" || !trailScoresAvailable) return false;
  const scores = trails.flatMap((t) =>
    t.status !== "closed" && typeof t.score === "number" && Number.isFinite(t.score) ? [t.score] : [],
  );
  return scores.length > 0 && scores.every((score) => score <= 0);
}
